import React, { Component } from "react";
import axios from "axios";
import Helmet from "react-helmet";
import { apiHost } from "../../api/apiClient";
import "../../styles/checklists/checklistNotifications.scss";

const emptyForm = {
  id: 0,
  email: "",
  receivePre: true,
  receivePost: true,
  receiveAutoLogout: false,
};

class ChecklistNotifications extends Component {
  constructor(props) {
    super(props);
    this.formCardRef = React.createRef();
    const query = new URLSearchParams(props.location.search);
    this.state = {
      recipients: [],
      customers: [],
      selectedCustomerId: query.get("customerId") || this.getStoredCustomerId(),
      currentUserEmail: "",
      canManageOrganisationRecipients: false,
      isSystemAdmin: this.getRole() === "systemadmin",
      form: { ...emptyForm },
      isLoading: false,
      isSaving: false,
      updatingId: 0,
      statusFilter: "all",
      error: "",
      message: "",
    };
  }

  componentDidMount() {
    if (this.state.isSystemAdmin) {
      this.loadCustomers();
    }
    this.loadRecipients();
  }

  getRole = () => String(localStorage.getItem("role") || "")
    .replace(/\s+/g, "")
    .toLowerCase();

  getStoredCustomerId = () => {
    try {
      const customer = JSON.parse(localStorage.getItem("customer"));
      return customer && customer.customerId ? customer.customerId : "";
    } catch (_) {
      return "";
    }
  };

  getAuthConfig = () => ({
    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
  });

  getCustomerQuery = () => this.state.selectedCustomerId
    ? `?customerId=${encodeURIComponent(this.state.selectedCustomerId)}`
    : "";

  getErrorMessage = (error, fallback) => {
    const response = error && error.response;
    if (!response) return fallback;
    if (typeof response.data === "string" && response.data) return response.data;
    return fallback + (response.status ? ` Status: ${response.status}.` : "");
  };

  loadCustomers = async () => {
    try {
      const response = await axios.get(
        `${apiHost}/api/Customer/GetAllCustomers?SortDirection=asc&SortString=name`,
        this.getAuthConfig()
      );
      this.setState({ customers: Array.isArray(response.data) ? response.data : [] });
    } catch (_) {
      this.setState({ error: "Unable to load the customer list." });
    }
  };

  loadRecipients = async () => {
    this.setState({ isLoading: true, error: "", message: "" });
    try {
      const response = await axios.get(
        `${apiHost}/api/ChecklistNotifications${this.getCustomerQuery()}`,
        this.getAuthConfig()
      );
      const data = response.data || {};
      const currentUserEmail = data.currentUserEmail || "";
      const canManageOrganisationRecipients = !!data.canManageOrganisationRecipients;
      this.setState((state) => ({
        recipients: Array.isArray(data.recipients) ? data.recipients : [],
        currentUserEmail,
        canManageOrganisationRecipients,
        isSystemAdmin: !!data.isSystemAdmin,
        isLoading: false,
        form: state.form.id
          ? state.form
          : { ...emptyForm, email: canManageOrganisationRecipients ? "" : currentUserEmail },
      }));
    } catch (error) {
      this.setState({
        recipients: [],
        isLoading: false,
        error: this.getErrorMessage(error, "Unable to load checklist notification recipients."),
      });
    }
  };

  selectCustomer = (event) => {
    const selectedCustomerId = event.target.value;
    const customer = this.state.customers.find((item) => item.id === selectedCustomerId);
    if (customer) {
      localStorage.setItem("customer", JSON.stringify({
        customerId: customer.id,
        customerName: customer.name,
      }));
    } else {
      localStorage.removeItem("customer");
    }

    const query = selectedCustomerId
      ? `?customerId=${encodeURIComponent(selectedCustomerId)}`
      : "";
    this.props.history.replace(`/checklist-notifications${query}`);
    this.setState({
      selectedCustomerId,
      form: { ...emptyForm },
      message: "",
      error: "",
    }, this.loadRecipients);
  };

  updateForm = (key, value) => {
    this.setState((state) => ({ form: { ...state.form, [key]: value } }));
  };

  resetForm = () => {
    this.setState({
      form: {
        ...emptyForm,
        email: this.state.canManageOrganisationRecipients ? "" : this.state.currentUserEmail,
      },
    });
  };

  saveRecipient = async (event) => {
    event.preventDefault();
    const form = this.state.form;
    const email = form.email.trim().toLowerCase();
    if (!email || email.indexOf("@") < 1) {
      this.setState({ error: "Enter a valid email address.", message: "" });
      return;
    }
    if (!form.receivePre && !form.receivePost && !form.receiveAutoLogout) {
      this.setState({ error: "Select at least one notification type.", message: "" });
      return;
    }

    this.setState({ isSaving: true, error: "", message: "" });
    try {
      await axios.post(
        `${apiHost}/api/ChecklistNotifications${this.getCustomerQuery()}`,
        { ...form, email },
        this.getAuthConfig()
      );
      this.resetForm();
      this.setState({
        isSaving: false,
        message: form.id ? "Recipient updated." : "Recipient added.",
      });
      await this.loadRecipients();
      this.setState({ message: form.id ? "Recipient updated." : "Recipient added." });
    } catch (error) {
      this.setState({
        isSaving: false,
        error: this.getErrorMessage(error, "Unable to save the recipient."),
      });
    }
  };

  editRecipient = (recipient) => {
    this.setState({
      form: {
        id: recipient.id,
        email: recipient.email,
        receivePre: recipient.receivePre,
        receivePost: recipient.receivePost,
        receiveAutoLogout: recipient.receiveAutoLogout,
      },
      error: "",
      message: "",
    }, () => {
      if (this.formCardRef.current) {
        this.formCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  setActive = async (recipient, active) => {
    if (!active && !window.confirm(`Disable checklist emails for ${recipient.email}?`)) {
      return;
    }

    this.setState({ updatingId: recipient.id, error: "", message: "" });
    try {
      await axios.put(
        `${apiHost}/api/ChecklistNotifications/${recipient.id}/active${this.getCustomerQuery()}`,
        { active },
        this.getAuthConfig()
      );
      this.setState((state) => ({
        recipients: state.recipients.map((item) => item.id === recipient.id
          ? { ...item, active }
          : item),
        updatingId: 0,
        message: active ? "Recipient enabled." : "Recipient disabled.",
      }));
    } catch (error) {
      this.setState({
        updatingId: 0,
        error: this.getErrorMessage(error, "Unable to update the recipient."),
      });
    }
  };

  renderToggle = (key, label, description) => (
    <label className="checklist-notification-toggle">
      <input
        type="checkbox"
        checked={this.state.form[key]}
        onChange={(event) => this.updateForm(key, event.target.checked)}
      />
      <span className="checklist-notification-switch" />
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
    </label>
  );

  setStatusFilter = (statusFilter) => {
    this.setState({ statusFilter });
  };

  renderStatusFilter = (value, label, count) => (
    <button
      type="button"
      className={this.state.statusFilter === value ? "active" : ""}
      onClick={() => this.setStatusFilter(value)}
    >
      {label} <b>{count}</b>
    </button>
  );

  render() {
    const {
      recipients, customers, selectedCustomerId, form, isSystemAdmin,
      canManageOrganisationRecipients, isLoading, isSaving, updatingId, statusFilter, error, message,
    } = this.state;
    const needsCustomer = isSystemAdmin && !selectedCustomerId;
    const activeCount = recipients.filter((recipient) => recipient.active).length;
    const disabledCount = recipients.length - activeCount;
    const filteredRecipients = recipients.filter((recipient) => (
      statusFilter === "all" ||
      (statusFilter === "active" && recipient.active) ||
      (statusFilter === "disabled" && !recipient.active)
    ));

    return (
      <div className="contentWrapper contentWrapperSmoothRelease checklist-notifications-page">
        <Helmet>
          <title>Checklist Notifications | VisionTrack</title>
        </Helmet>

        <section className="checklist-notifications-header">
          <div className="checklist-notifications-title">
            <h1>Checklist Notifications</h1>
            <p>Choose who receives Pre-Start, Post-Trip and Driver Auto Logout email alerts.</p>
          </div>
          {isSystemAdmin && (
            <div className="checklist-notifications-customer">
              <label>
                <span>Customer</span>
                <select value={selectedCustomerId} onChange={this.selectCustomer}>
                  <option value="">All customers — select one to add or edit</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>{customer.name}</option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <span>{recipients.length} recipient{recipients.length === 1 ? "" : "s"}</span>
        </section>

        <section
          ref={this.formCardRef}
          className={`checklist-notification-form-card ${needsCustomer ? "disabled" : ""} ${form.id ? "editing" : ""}`}
        >
          <div className="checklist-notification-form-heading">
            <div>
              <div className="checklist-notification-form-title">
                <h2>{form.id ? "Edit email recipient" : "Add email recipient"}</h2>
                {form.id > 0 && <span><i className="fa fa-pencil" /> Editing</span>}
              </div>
              <p>
                {canManageOrganisationRecipients
                  ? "The address can be an individual mailbox or a distribution list."
                  : "You can subscribe only your own login email address."}
              </p>
            </div>
            {form.id > 0 && <button type="button" onClick={this.resetForm}>Cancel edit</button>}
          </div>

          <form onSubmit={this.saveRecipient}>
            <label className="checklist-notification-email">
              <span>Email address</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => this.updateForm("email", event.target.value)}
                placeholder="fleet@example.com"
                readOnly={!canManageOrganisationRecipients}
                disabled={needsCustomer}
              />
            </label>
            <div className="checklist-notification-types">
              {this.renderToggle("receivePre", "Pre-Start", "Completed vehicle pre-start checks")}
              {this.renderToggle("receivePost", "Post-Trip", "Completed driver post-trip checks")}
              {this.renderToggle("receiveAutoLogout", "Auto Logout", "Automatic driver logout events")}
            </div>
            <button className="checklist-notification-save" disabled={isSaving || needsCustomer}>
              {isSaving ? "Saving…" : form.id ? "Save changes" : "Add recipient"}
            </button>
          </form>
          {needsCustomer && <p className="checklist-notification-customer-required">Select a customer before adding or editing a recipient.</p>}
        </section>

        {error && <div className="checklist-notification-alert error">{error}</div>}
        {message && <div className="checklist-notification-alert success">{message}</div>}

        <section className="checklist-notification-list">
          <div className="checklist-notification-list-heading">
            <h2>Recipients</h2>
            <div className="checklist-notification-list-controls">
              <div className="checklist-notification-status-filter">
                {this.renderStatusFilter("all", "All", recipients.length)}
                {this.renderStatusFilter("active", "Active", activeCount)}
                {this.renderStatusFilter("disabled", "Disabled", disabledCount)}
              </div>
              <button className="checklist-notification-refresh" type="button" onClick={this.loadRecipients} disabled={isLoading}>
                {isLoading ? "Loading…" : "Refresh"}
              </button>
            </div>
          </div>
          <div className="checklist-notification-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  {isSystemAdmin && <th>Customer</th>}
                  <th>Pre-Start</th>
                  <th>Post-Trip</th>
                  <th>Auto Logout</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && filteredRecipients.map((recipient) => (
                  <tr key={recipient.id} className={recipient.active ? "" : "disabled"}>
                    <td><strong>{recipient.email}</strong></td>
                    {isSystemAdmin && <td>{recipient.customerName || "Unknown customer"}</td>}
                    <td>{recipient.receivePre ? <i className="fa fa-check" /> : "—"}</td>
                    <td>{recipient.receivePost ? <i className="fa fa-check" /> : "—"}</td>
                    <td>{recipient.receiveAutoLogout ? <i className="fa fa-check" /> : "—"}</td>
                    <td>
                      <span className={`checklist-notification-status ${recipient.active ? "active" : "disabled"}`}>
                        {recipient.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td>
                      <div className="checklist-notification-actions">
                        <button
                          type="button"
                          onClick={() => this.editRecipient(recipient)}
                          disabled={needsCustomer || updatingId === recipient.id}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className={recipient.active ? "disable" : "enable"}
                          onClick={() => this.setActive(recipient, !recipient.active)}
                          disabled={needsCustomer || updatingId === recipient.id}
                        >
                          {updatingId === recipient.id
                            ? "Saving…"
                            : recipient.active ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!isLoading && filteredRecipients.length === 0 && (
                  <tr>
                    <td colSpan={isSystemAdmin ? 7 : 6} className="checklist-notification-empty">
                      {recipients.length
                        ? `No ${statusFilter} recipients match this filter.`
                        : "No checklist notification recipients are configured."}
                    </td>
                  </tr>
                )}
                {isLoading && (
                  <tr>
                    <td colSpan={isSystemAdmin ? 7 : 6} className="checklist-notification-empty">Loading recipients…</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    );
  }
}

export default ChecklistNotifications;
